import { Component, Input } from '@angular/core';

/**
 * Read-only display of the AI tags stored on an Item or Challenge.
 *
 * Renders `attributes` with a keyvalue loop rather than a fixed field list, because the
 * shape is server-defined and differs per asset type (item_hard carries
 * style/material/pattern/shape/color, item_soft carries trait/color, challenge carries
 * style/time_of_day/climate/room_type). Anything new the service starts returning shows
 * up here without a code change.
 *
 * Note this is NOT the same as the older "AI Metadata" column, which renders the legacy
 * `vizMetadata` from the long-disabled visual tagger. The two have different shapes and
 * different provenance; they are deliberately separate components.
 */
@Component({
  selector: 'app-ai-tags-panel',
  templateUrl: './ai-tags-panel.component.html',
  styleUrls: ['./ai-tags-panel.component.sass'],
})
export class AiTagsPanelComponent {
  @Input() aiTags: any;

  /** Hides the whole body and shows the empty state instead. */
  get isEmpty(): boolean {
    return !this.aiTags?.taggedAt && !this.attributeGroups.length && !this.aiTags?.description;
  }

  /** Attribute entries that actually have values, so empty arrays do not render a bare label. */
  get attributeGroups(): Array<{ key: string; values: string[] }> {
    const attributes = this.aiTags?.attributes || {};

    return Object.keys(attributes)
      .map((key) => ({ key, values: (attributes[key] || []).filter((value: any) => !!value) }))
      .filter((group) => group.values.length > 0);
  }

  get objectTags(): string[] {
    return this.aiTags?.objectTags || [];
  }

  get warnings(): string[] {
    return this.aiTags?.warnings || [];
  }

  /** "time_of_day" reads badly as a label; the service's keys are not display strings. */
  label(key: string): string {
    return key.replace(/_/g, ' ');
  }
}
